import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { CameraInfo, fetchFrigateCameras, getFrigateSnapshotUrl, controlCameraPtz, postCameraLight } from '../services/api';
import { triggerHaptic } from '../services/purrSynthesizer';
import {
  Video,
  Router,
  RefreshCw,
  Maximize2,
  X,
  ExternalLink,
  ShieldCheck,
  Flame,
  Radio,
  PhoneCall,
  Activity,
  Server,
  Cpu,
  Wifi,
  Sliders,
  Lightbulb,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

export const SmartHomeDeckView: React.FC = () => {
  const { setActiveTab } = useApp();
  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [selectedCamera, setSelectedCamera] = useState<CameraInfo | null>(null);
  const [ptzFeedback, setPtzFeedback] = useState<string | null>(null);

  const handlePtzAction = async (direction: 'up' | 'down' | 'left' | 'right') => {
    if (!selectedCamera) return;
    triggerHaptic(20);
    try {
      const res = await controlCameraPtz(selectedCamera.id, { action: 'step', direction });
      setPtzFeedback(res.ok ? ('Moved ' + direction.toUpperCase()) : 'PTZ busy');
    } catch {
      setPtzFeedback('PTZ step');
    } finally {
      setTimeout(() => setPtzFeedback(null), 2000);
    }
  };

  const handlePresetAction = async (preset: string) => {
    if (!selectedCamera) return;
    triggerHaptic([20, 30]);
    try {
      const res = await controlCameraPtz(selectedCamera.id, { action: 'preset', preset });
      setPtzFeedback(res.ok ? ('Preset: ' + preset) : 'Preset busy');
    } catch {
      setPtzFeedback('Preset sent');
    } finally {
      setTimeout(() => setPtzFeedback(null), 2500);
    }
  };
  const [cacheBuster, setCacheBuster] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshingCams, setRefreshingCams] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Real Household & Network Hardware Inventory
  const [networkDevices, setNetworkDevices] = useState([
    {
      id: 'tplink-gw',
      name: 'TP-Link Network Gateway',
      type: 'router',
      ip: '192.168.68.1',
      status: 'online',
      latencyMs: 1.8,
      details: 'Dual-Band Wi-Fi 6 Mesh Core • Gigabit Uplink'
    },
    {
      id: 'alexa-echo',
      name: 'Amazon Alexa (Echo Hub)',
      type: 'smart_speaker',
      ip: '192.168.68.82',
      status: 'online',
      latencyMs: 4.2,
      details: 'SSDP / UPnP Media Node • Voice Relay'
    },
    {
      id: 'magicjack-voip',
      name: 'MagicJack Home Line',
      type: 'voip',
      ip: '192.168.68.95',
      status: 'online',
      latencyMs: 3.1,
      details: 'SIP Telephony Gateway • Voice over IP'
    },
    {
      id: 'echodedicated-srv',
      name: 'echodedicated (Host)',
      type: 'server',
      ip: '192.168.68.50',
      status: 'online',
      latencyMs: 0.9,
      details: 'Ubuntu 22.04 LTS • Frigate, Immich, Qdrant, Pi-hole'
    },
  ]);

  useEffect(() => {
    fetchFrigateCameras().then((cams) => setCameras(cams));
  }, []);

  // Auto-refresh camera snapshots every 6 seconds when active and visible
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      setCacheBuster(Date.now());
    }, 6000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleManualRefresh = () => {
    setRefreshingCams(true);
    triggerHaptic(20);
    setCacheBuster(Date.now());
    setImageErrors({});
    fetchFrigateCameras().then((cams) => {
      setCameras(cams);
      setTimeout(() => setRefreshingCams(false), 600);
    });
  };

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4 pb-16">
      {/* Smart Home Header */}
      <div className="rounded-2xl p-3.5 glass-panel-gold border border-fissal-gold/30 shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-amber-500/20 border border-fissal-gold/40 flex items-center justify-center text-fissal-gold">
            <Router className="w-4 h-4" />
          </div>
          <div>
            <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
              HOUSEHOLD & NETWORK DECK
            </h2>
            <p className="text-[10px] text-slate-400 font-mono">
              Frigate NVR Feeds • TP-Link • Network Sentinel
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`px-2 py-1 rounded-lg text-[10px] font-mono border transition-all ${
              autoRefresh
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                : 'bg-white/5 border-white/10 text-slate-400'
            }`}
            title="Auto-refresh camera snapshots"
          >
            {autoRefresh ? 'LIVE 4s' : 'PAUSED'}
          </button>
          <button
            onClick={handleManualRefresh}
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all"
            title="Refresh All Cameras"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshingCams ? 'animate-spin text-fissal-gold' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stage Rig Quick Portal Banner */}
      <div className="rounded-2xl p-4 bg-gradient-to-r from-red-950/50 via-amber-950/40 to-slate-900 border border-amber-500/40 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600/20 border border-red-500/40 flex items-center justify-center text-red-400 shadow-quantum-glow animate-pulse">
            <Flame className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-cinzel text-xs font-bold text-amber-300 tracking-wider">
                GUARDIAN STAGE RIG ENGINE
              </h3>
              <span className="text-[9px] px-1.5 py-0.5 rounded font-mono font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
                ACTIVE
              </span>
            </div>
            <p className="text-[11px] text-slate-300 mt-0.5">
              Altman 360Q Lekos, Behringer X32 OSC, DBO Blackout, & Soundcheck RTA
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            triggerHaptic([30, 40]);
            setActiveTab('stagerig');
          }}
          className="px-3.5 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs font-mono shadow-quantum-glow flex items-center gap-1.5 transition-all"
        >
          <Sliders className="w-3.5 h-3.5" />
          Open Deck →
        </button>
      </div>

      {/* Frigate Camera Grid */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between px-1">
          <div className="flex items-center gap-1.5">
            <Video className="w-4 h-4 text-sky-400" />
            <h3 className="text-xs font-bold text-white uppercase tracking-wider font-cinzel">
              Frigate Security Cameras ({cameras.length})
            </h3>
          </div>
          <a
            href="https://frigate.ech-o.net"
            target="_blank"
            rel="noreferrer"
            className="text-[10px] text-sky-400 hover:underline flex items-center gap-1 font-mono"
          >
            Full NVR <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>

        <div className="grid grid-cols-1 gap-3">
          {cameras.map((cam) => {
            const snapshotUrl = getFrigateSnapshotUrl(cam.id, cacheBuster);
            const isError = imageErrors[cam.id];

            return (
              <div
                key={cam.id}
                className="glass-panel rounded-2xl overflow-hidden border border-white/10 shadow-lg relative group"
              >
                {/* Camera Video / Snapshot Viewport */}
                <div
                  className="relative aspect-video bg-black/80 overflow-hidden cursor-pointer flex items-center justify-center"
                  onClick={() => setSelectedCamera(cam)}
                >
                  <img
                    src={snapshotUrl}
                    alt={cam.name.replace('Dogshop - ', '').replace('Lycan - ', '')}
                    className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${
                      isError ? 'hidden' : 'block'
                    }`}
                    onError={() => {
                      setImageErrors((prev) => ({ ...prev, [cam.id]: true }));
                    }}
                  />

                  {/* Fallback Viewport */}
                  {isError && (
                    <div className="flex flex-col items-center justify-center text-slate-500 p-4 text-center">
                      <Video className="w-8 h-8 mb-1.5 opacity-40" />
                      <span className="text-xs font-mono">Stream Standby</span>
                      <span className="text-[10px] opacity-60">Checking zone trigger...</span>
                    </div>
                  )}

                  {/* Camera Name & Live Badge */}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 backdrop-blur-md px-2.5 py-1 rounded-lg border border-white/10">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[11px] font-mono font-bold text-white tracking-wide">
                      {cam.name.replace('Dogshop - ', '').replace('Lycan - ', '')}
                    </span>
                  </div>

                  {/* Zone Tag */}
                  {cam.zone && (
                    <div className="absolute top-2 right-2 bg-amber-500/20 backdrop-blur-md border border-fissal-gold/40 px-2 py-0.5 rounded text-[10px] font-mono text-fissal-goldLight">
                      {cam.zone}
                    </div>
                  )}

                  {/* Zoom Overlay Icon */}
                  <div className="absolute bottom-2 right-2 p-1.5 rounded-lg bg-black/50 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Maximize2 className="w-3.5 h-3.5" />
                  </div>
                </div>

                {/* Camera Details Strip */}
                <div className="p-2.5 bg-[#0a0e17] flex items-center justify-between text-[11px] font-mono text-slate-300 border-t border-white/5">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-slate-400">
                      IP: <span className="text-white">{cam.ip || 'DHCP'}</span>
                    </span>
                    <span>FPS: {cam.fps || '15'}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 text-[10px] font-semibold">
                      DETECT ONLINE
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Network Hardware & Infrastructure Nodes */}
      <div className="glass-panel rounded-2xl p-3.5 border border-white/10 space-y-3 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Router className="w-4 h-4 text-fissal-gold" />
            <h3 className="font-semibold text-xs text-white uppercase tracking-wider font-cinzel">
              Network Hardware & Telemetry
            </h3>
          </div>
          <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/30">
            {networkDevices.length} Nodes Active
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {networkDevices.map((dev) => (
            <div
              key={dev.id}
              className="p-3 rounded-xl bg-slate-900/60 border border-white/5 flex flex-col justify-between"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-300">
                    {dev.type === 'router' && <Wifi className="w-3.5 h-3.5" />}
                    {dev.type === 'smart_speaker' && <Radio className="w-3.5 h-3.5" />}
                    {dev.type === 'voip' && <PhoneCall className="w-3.5 h-3.5" />}
                    {dev.type === 'server' && <Server className="w-3.5 h-3.5" />}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-white">{dev.name}</div>
                    <div className="text-[10px] text-slate-400 font-mono">{dev.ip}</div>
                  </div>
                </div>
                <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded">
                  {dev.latencyMs}ms
                </span>
              </div>
              <div className="text-[10px] text-slate-400 mt-2 font-mono border-t border-white/5 pt-1.5">
                {dev.details}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Zoom / Fullscreen Modal for Cameras */}
      {selectedCamera && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 bg-black/90 backdrop-blur-md animate-in fade-in duration-150">
          <div className="w-full max-w-lg rounded-2xl glass-panel-gold overflow-hidden border border-fissal-gold/50 shadow-2xl flex flex-col">
            <div className="p-3 bg-black/80 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-sky-400" />
                <span className="font-cinzel text-xs font-bold text-white">
                  {selectedCamera.name} — Live Snapshot
                </span>
              </div>
              <button
                onClick={() => setSelectedCamera(null)}
                className="p-1 rounded-lg hover:bg-white/10 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="relative aspect-video bg-black flex items-center justify-center">
              <img
                src={getFrigateSnapshotUrl(selectedCamera.id, cacheBuster)}
                alt={selectedCamera.name}
                className="w-full h-full object-contain"
              />
              {ptzFeedback && (
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/80 border border-fissal-gold/50 text-fissal-gold text-[10px] font-mono animate-pulse">
                  {ptzFeedback}
                </div>
              )}
            </div>

            {/* PTZ Directional Motor in Modal */}
            {selectedCamera.ptz && (
              <div className="p-3 bg-slate-900/80 border-t border-white/10 space-y-2">
                <div className="text-[10px] font-mono text-slate-400 uppercase tracking-wider text-center">
                  ONVIF PTZ Directional Stepper
                </div>
                <div className="flex items-center justify-center gap-3">
                  <div className="grid grid-cols-3 gap-1.5 w-32">
                    <div />
                    <button
                      onClick={() => handlePtzAction('up')}
                      className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <div />
                    <button
                      onClick={() => handlePtzAction('left')}
                      className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="p-2 rounded-xl bg-black/40 flex items-center justify-center text-[9px] font-mono text-fissal-gold">
                      PTZ
                    </div>
                    <button
                      onClick={() => handlePtzAction('right')}
                      className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <div />
                    <button
                      onClick={() => handlePtzAction('down')}
                      className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <div />
                  </div>

                  {/* Presets if available */}
                  {selectedCamera.presets && selectedCamera.presets.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      {selectedCamera.presets.map((preset) => (
                        <button
                          key={preset}
                          onClick={() => handlePresetAction(preset)}
                          className="px-2.5 py-1 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-mono capitalize text-slate-300"
                        >
                          📍 {preset}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="p-3 bg-black/80 border-t border-white/10 flex items-center justify-between">
              <div className="text-[11px] font-mono text-slate-300">
                Zone: <span className="text-fissal-gold">{selectedCamera.zone}</span>
              </div>
              <a
                href={`https://frigate.ech-o.net/#${selectedCamera.id}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs flex items-center gap-1.5 transition-all"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open in Frigate NVR
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};